import React, { useState, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { ReactComponent as RuniumIcon } from '../assets/runium_icon.svg';
import OnboardingFooter from './Onboarding/OnboardingFooter';
import { ReactComponent as BrainIcon } from '../assets/brain_icon.svg';
import { getApiUrl } from '../utils/apiUtils';
import { debugLog, debugWarn, debugError } from '../utils/debug';
import DatePicker from './DatePicker';

const Onboarding = ({ onComplete }) => {
  const { user } = useUser();
  const { getToken } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingExistingData, setIsLoadingExistingData] = useState(false);
  const [hasSetInitialStep, setHasSetInitialStep] = useState(false);
  
  // Form state - updated structure to match mockup
  const [formData, setFormData] = useState({
    runningReasons: [],
    personalBests: {
      pbsOrder: [],
      lifetimePBs: {},
      recentPBs: {},
      lifetimeYears: {},
      recentYears: {}
    },
    weeklyVolume: { 
      type: 'distance', 
      value: 30, 
      unit: 'km', 
      minutes: 180
    },
    targetRace: { 
      distance: '', 
      targetTime: null, 
      justFinish: false,
      raceDate: ''
    },
    weeklyDays: 3,
    canRunTwice: false,
    irregularSchedule: false,
    preferences: [],
    checkInFrequency: '',
    additionalNotes: '',
    // Training start date
    trainingStartDate: ''
  });

  // World records and PB ranges (in seconds)
  const WORLD_RECORDS = {
    '5K': 755, // 12:35
    '10K': 1571, // 26:11
    'Half Marathon': 3402, // 56:42
    'Marathon': 7235 // 2:00:35
  };
  
  const PB_RANGES = {
    '5K': { min: WORLD_RECORDS['5K'], max: 3000, step: 1, label: '5K' },
    '10K': { min: WORLD_RECORDS['10K'], max: 5400, step: 1, label: '10K' },
    'Half Marathon': { min: WORLD_RECORDS['Half Marathon'], max: 10800, step: 1, label: 'Half Marathon' },
    'Marathon': { min: WORLD_RECORDS['Marathon'], max: 21600, step: 1, label: 'Marathon' },
    'Other Distance': { min: 0, max: 1000, step: 1, label: 'Other Distance' }
  };

  // Helper function to format time from seconds
  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const paddedSeconds = seconds.toString().padStart(2, '0');
    const paddedMinutes = minutes.toString().padStart(2, '0');
    if (hours > 0) return `${hours}:${paddedMinutes}:${paddedSeconds}`;
    return `${paddedMinutes}:${paddedSeconds}`;
  };

  // Check for existing onboarding data when component mounts
  useEffect(() => {
    const checkForExistingData = async () => {
      try {
        setIsLoadingExistingData(true);
        const token = await getToken();
        
        if (!token) {
          debugLog('📋 [ONBOARDING] No token available, skipping data fetch');
          return;
        }
        
        const API_URL = getApiUrl();
        
        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          debugLog('📋 [ONBOARDING] Request timeout, continuing without existing data');
          controller.abort();
        }, 5000); // Increased to 5 seconds
        
        const response = await fetch(`${API_URL}/user/onboarding-data`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          const existingData = data.onboarding_data;
          
          if (existingData && Object.keys(existingData).length > 0) {
            debugLog('📋 [ONBOARDING] Found existing onboarding data, loading it:', existingData);
            
            // Convert runningReasons from object format to array format if needed
            let processedData = { ...existingData };
            if (existingData.runningReasons && !Array.isArray(existingData.runningReasons)) {
              // Backend sends: { train_for_a_race: true, achieve_personal_best: null, ... }
              // Frontend expects: ['train_for_a_race', 'achieve_personal_best', ...]
              const reasonsArray = Object.keys(existingData.runningReasons).filter(
                key => existingData.runningReasons[key] === true || existingData.runningReasons[key] === 'Yes'
              );
              processedData.runningReasons = reasonsArray;
              debugLog('📋 [ONBOARDING] Converted runningReasons from object to array:', reasonsArray);
            }
            
            // Convert preferences from object format to array format if needed
            if (existingData.preferences && !Array.isArray(existingData.preferences)) {
              const prefsArray = Object.keys(existingData.preferences).filter(
                key => existingData.preferences[key] === true || existingData.preferences[key] === 'Yes'
              );
              processedData.preferences = prefsArray;
              debugLog('📋 [ONBOARDING] Converted preferences from object to array:', prefsArray);
            }
            
            // Ensure personalBests has the correct structure
            if (existingData.personalBests) {
              const pbs = existingData.personalBests;
              // Check if it's in the old format or missing required fields
              if (!pbs.pbsOrder || !pbs.lifetimePBs || !pbs.recentPBs) {
                // Initialize with default structure
                processedData.personalBests = {
                  pbsOrder: pbs.pbsOrder || [],
                  lifetimePBs: pbs.lifetimePBs || pbs.lifetime || {},
                  recentPBs: pbs.recentPBs || pbs.recent || {},
                  lifetimeYears: pbs.lifetimeYears || {},
                  recentYears: pbs.recentYears || {}
                };
                debugLog('📋 [ONBOARDING] Normalized personalBests structure:', processedData.personalBests);
              } else {
                // Ensure all required fields exist (even if empty)
                processedData.personalBests = {
                  pbsOrder: pbs.pbsOrder || [],
                  lifetimePBs: pbs.lifetimePBs || {},
                  recentPBs: pbs.recentPBs || {},
                  lifetimeYears: pbs.lifetimeYears || {},
                  recentYears: pbs.recentYears || {}
                };
              }
            } else {
              // If personalBests doesn't exist, use default structure
              processedData.personalBests = {
                pbsOrder: [],
                lifetimePBs: {},
                recentPBs: {},
                lifetimeYears: {},
                recentYears: {}
              };
            }
            
            // Load existing data into form state
            setFormData(prev => ({ ...prev, ...processedData }));
            
            // We'll set the current step in a separate useEffect that runs after formData is updated
            // This ensures the dynamic steps are calculated with the loaded data
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          debugLog('📋 [ONBOARDING] Request aborted due to timeout');
        } else {
          debugError('📋 [ONBOARDING] Error loading existing onboarding data:', error);
        }
      } finally {
        setIsLoadingExistingData(false);
      }
    };

    checkForExistingData();
  }, []);

  // Separate useEffect to set the current step after formData is loaded
  useEffect(() => {
    // Only run this if we have loaded existing data and we're still at step 0 and haven't set initial step yet
    if (!isLoadingExistingData && currentStep === 0 && !hasSetInitialStep && formData && Object.keys(formData).length > 0) {
      // Prevent duplicate logs from React StrictMode
      const logKey = 'onboarding_check_data';
      if (sessionStorage.getItem(logKey)) return;
      sessionStorage.setItem(logKey, 'true');
      setTimeout(() => sessionStorage.removeItem(logKey), 500);
      
      // Check if we have meaningful existing data (not just default values)
      const hasExistingData = formData.runningReasons && formData.runningReasons.length > 0;
      
      if (hasExistingData) {
        // Find the preferences step index (considerations screen) in the dynamic steps
        const tempVisibleSteps = getDynamicSteps();
        const preferencesStepIndex = tempVisibleSteps.findIndex(step => step.id === 'preferences');
        if (preferencesStepIndex !== -1) {
          debugLog('🎯 [ONBOARDING] Starting at considerations screen (step', preferencesStepIndex, ')');
          setCurrentStep(preferencesStepIndex);
          setHasSetInitialStep(true);
        }
      } else {
        debugLog('📝 [ONBOARDING] No existing data found, starting from beginning');
        setHasSetInitialStep(true);
      }
    }
  }, [formData, isLoadingExistingData, currentStep, hasSetInitialStep]);

  // Define all onboarding steps - updated to match mockup flow
  const baseOnboardingSteps = [
    {
      id: 'runningReasons',
      title: "Why do you run?",
      subtitle: "Select all that apply",
      type: 'multiple-choice',
      choices: [
        { id: 'train_for_a_race', title: 'Train for a race', icon: '🎯' },
        { id: 'achieve_personal_best', title: 'Achieve personal best', icon: '🏆' },
        { id: 'improve_fitness', title: 'Improve fitness', icon: '❤️' },
        { id: 'lose_weight', title: 'Lose weight', icon: '⚖️' },
        { id: 'stay_healthy', title: 'Stay healthy', icon: '🌟' },
        { id: 'run_for_enjoyment', title: 'Run for enjoyment', icon: '✨' }
      ]
    },
    {
      id: 'weeklyVolume',
      title: "How much do you run weekly?",
      subtitle: "Tell us about your current running volume",
      type: 'form',
      content: null // Will be replaced in getDynamicSteps
    },
    {
      id: 'weeklyDays',
      title: "Days you can run",
      subtitle: "",
      type: 'form',
      content: (
        <WeeklyDaysForm formData={formData} setFormData={setFormData} />
      )
    },
    {
      id: 'preferences',
      title: "Other considerations",
      subtitle: "Select all that apply (optional)",
      type: 'form',
      content: (
        <PreferencesForm formData={formData} setFormData={setFormData} />
      )
    },
    {
      id: 'complete',
      title: "Welcome to your training journey 🎉",
      type: 'completion',
      content: (
        <div className="text-center space-y-6">
          {/* Static brain badge — confirmation lives in the words below, not the dance */}
          <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto bg-blue-500/15 ring-1 ring-blue-500/30 rounded-full flex items-center justify-center">
            <BrainIcon className="w-10 h-10 sm:w-12 sm:h-12 text-blue-400" />
          </div>

          {/* Profile summary card — single source of confirmation */}
          <div className="bg-gray-800/60 rounded-xl p-6 space-y-3 text-left max-w-lg mx-auto border border-gray-700/50">
            <h4 className="font-semibold text-white text-center">Your training profile</h4>

            <div className="grid grid-cols-1 gap-3">
              {formData.weeklyVolume && formData.weeklyVolume.value && (
                <div className="flex items-center justify-between bg-gray-900/40 rounded-lg p-3">
                  <span className="text-gray-400 text-sm">Weekly volume</span>
                  <span className="text-white font-medium">
                    {formData.weeklyVolume.type === 'distance'
                      ? `${formData.weeklyVolume.value} ${formData.weeklyVolume.unit}`
                      : `${Math.floor(formData.weeklyVolume.minutes / 60)}h ${formData.weeklyVolume.minutes % 60}m`
                    }
                  </span>
                </div>
              )}

              {formData.targetRace && formData.targetRace.distance && (
                <div className="flex items-center justify-between bg-gray-900/40 rounded-lg p-3">
                  <span className="text-gray-400 text-sm">Target race</span>
                  <span className="text-white font-medium">{formData.targetRace.distance}</span>
                </div>
              )}

              {formData.selectedDays && formData.selectedDays.length > 0 && (
                <div className="flex items-center justify-between bg-gray-900/40 rounded-lg p-3">
                  <span className="text-gray-400 text-sm">Training days</span>
                  <span className="text-white font-medium">{formData.selectedDays.length} days/week</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )
    }
  ];

    // Navigation helpers
  const handleChoice = (choiceId, isMultiple = false) => {
    setIsAnimating(true);

    if (isMultiple) {
      // Handle multiple choice questions
      const currentChoices = formData[currentStepData.id] || [];
      const newChoices = currentChoices.includes(choiceId)
        ? currentChoices.filter(id => id !== choiceId)
        : [...currentChoices, choiceId];
      
      setFormData(prev => ({ ...prev, [currentStepData.id]: newChoices }));
      setIsAnimating(false);
      return;
    } else {
      // Handle single choice questions - auto-advance for single choice only
      setFormData(prev => ({ ...prev, [currentStepData.id]: choiceId }));
      
      setTimeout(() => {
        if (currentStep < visibleSteps.length - 1) {
          setCurrentStep(currentStep + 1);
        }
        setIsAnimating(false);
      }, 300);
    }
  };

  const handleNext = () => {
    // Prevent navigation during animation
    if (isAnimating) return;
    
    setIsAnimating(true);
    
    if (currentStep < visibleSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
    
    // Reset animation state after CSS transition
    setTimeout(() => {
      setIsAnimating(false);
    }, 50);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceedWithMultiple = () => {
    // Multiple choice validation
    if (currentStepData.type === 'multiple-choice') {
      return formData[currentStepData.id] && formData[currentStepData.id].length > 0;
    }
    
    // Personal Bests validation - require at least one distance with time
    if (currentStepData.id === 'personalBests') {
      const pbs = formData.personalBests;
      return pbs.pbsOrder && pbs.pbsOrder.length > 0;
    }
    
    // Goal Race validation - require distance selection
    if (currentStepData.id === 'targetRace') {
      return formData.targetRace && formData.targetRace.distance;
    }
    
    // Weekly days validation - require days selection or irregular schedule
    if (currentStepData.id === 'weeklyDays') {
      return formData.irregularSchedule || (formData.selectedDays && formData.selectedDays.length > 0);
    }
    
    return true;
  };

  // Get dynamic steps based on user selections
  const getDynamicSteps = () => {
    const createWeeklyVolumeStep = () => ({
      id: 'weeklyVolume',
      title: "How much do you run weekly?",
      subtitle: "",
      type: 'form',
      content: (
        <WeeklyVolumeForm formData={formData} setFormData={setFormData} onAutoAdvance={handleNext} />
      )
    });
    
    
    let steps = [...baseOnboardingSteps];
    // Ensure runningReasons is always an array (handle both object and array formats)
    let runningReasons = formData.runningReasons || [];
    if (!Array.isArray(runningReasons)) {
      // Convert object format to array format
      runningReasons = Object.keys(runningReasons).filter(
        key => runningReasons[key] === true || runningReasons[key] === 'Yes'
      );
      debugWarn('⚠️ [ONBOARDING] runningReasons was not an array, converted:', runningReasons);
    }
    
    // Replace weeklyVolume step with one that has onAutoAdvance
    const volumeIndex = steps.findIndex(step => step.id === 'weeklyVolume');
    if (volumeIndex !== -1) {
      steps[volumeIndex] = createWeeklyVolumeStep();
    }
    
    
    // Add Personal Bests step if user wants to achieve personal best OR train for a race
    if (runningReasons.includes('achieve_personal_best') || runningReasons.includes('train_for_a_race')) {
      const personalBestsStep = {
        id: 'personalBests',
        title: "Your personal bests",
        subtitle: "Add at least one distance and year",
        type: 'form',
        content: (
          <PersonalBestsForm formData={formData} setFormData={setFormData} />
        )
      };
      steps.splice(1, 0, personalBestsStep); // Insert right after runningReasons
    }
    
    // Add Goal Race step if user wants to train for a race
    if (runningReasons.includes('train_for_a_race')) {
      const goalRaceStep = {
        id: 'targetRace',
        title: "Your goal race",
        subtitle: "",
        type: 'form',
        content: (
          <GoalRaceForm formData={formData} setFormData={setFormData} />
        )
      };
      // Insert before weeklyVolume step
      const currentVolumeIndex = steps.findIndex(step => step.id === 'weeklyVolume');
      steps.splice(currentVolumeIndex, 0, goalRaceStep);
    }
    
    return steps;
  };

  const visibleSteps = getDynamicSteps();
  const currentStepData = visibleSteps[currentStep];

  // Auto-complete after showing completion screen
  useEffect(() => {
    if (currentStepData && currentStepData.id === 'complete' && !isSubmitting) {
      debugLog('Completion screen displayed, setting timers for auto-complete');
      debugLog('Current step:', currentStep, 'Step ID:', currentStepData.id, 'isSubmitting:', isSubmitting);
      
      // Removed automatic timer - user must click Next button
      
      // No automatic timers - user controls navigation
    }
  }, [currentStep, currentStepData?.id]);

  // Helper functions for data transformation
  const extractPersonalBest = (personalBests, type) => {
    if (!personalBests || !personalBests.pbsOrder || personalBests.pbsOrder.length === 0) {
      return null;
    }
    
    const pbs = type === 'lifetime' ? personalBests.lifetimePBs : personalBests.recentPBs;
    const years = type === 'lifetime' ? personalBests.lifetimeYears : personalBests.recentYears;
    
    // Find the first distance with a time
    for (const distance of personalBests.pbsOrder) {
      if (pbs[distance] && years[distance]) {
        const timeInSeconds = parseInt(pbs[distance]);
        const year = years[distance];
        const formattedTime = formatTime(timeInSeconds);
        return `${distance}: ${formattedTime} (${year})`;
      }
    }
    
    return null;
  };
  
  const formatWeeklyVolume = (weeklyVolume) => {
    if (!weeklyVolume) return null;
    
    if (weeklyVolume.type === 'distance') {
      // Convert to km for backend standardization, but keep original unit for display
      const valueInKm = weeklyVolume.unit === 'mi' ? Math.round(weeklyVolume.value * 1.609) : weeklyVolume.value;
      return `${valueInKm} km`;
    } else {
      const hours = Math.floor(weeklyVolume.minutes / 60);
      const minutes = weeklyVolume.minutes % 60;
      return `${hours}h ${minutes}m`;
    }
  };
  
  const formatTargetTime = (targetTime) => {
    if (!targetTime) return null;
    
    if (targetTime.totalSeconds) {
      return formatTime(targetTime.totalSeconds);
    } else if (targetTime.hours !== undefined && targetTime.minutes !== undefined) {
      const totalSeconds = (targetTime.hours * 3600) + (targetTime.minutes * 60);
      return formatTime(totalSeconds);
    }
    
    return null;
  };
  
  const formatGoalRaceDate = (raceDate) => {
    if (!raceDate) return null;
    
    try {
      const date = new Date(raceDate);
      if (isNaN(date.getTime())) return null;
      
      // Format as readable date
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      debugError('Error formatting race date:', error);
      return null;
    }
  };
  
  const formatTrainingDays = (selectedDays, irregularSchedule) => {
    if (irregularSchedule) {
      return "Irregular schedule - no fixed days";
    }
    
    if (!selectedDays || selectedDays.length === 0) {
      return "No training days selected";
    }
    
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return selectedDays.map(dayIndex => dayNames[dayIndex]).join(', ');
  };

  // Submit to backend and complete onboarding
  const handleComplete = async () => {
    // Prevent multiple simultaneous calls
    if (isSubmitting) {
      debugLog('⚠️ [ONBOARDING] Already submitting, skipping duplicate call');
      return;
    }
    
    debugLog('🎯 [ONBOARDING] Starting submission...');
    setIsSubmitting(true);
    
    try {
      const API_URL = getApiUrl();
      
      // Transform form data to match backend expectations and ensure LLM can access all fields
      const transformedData = {
        // Basic info
        runningReasons: formData.runningReasons,
        weeklyDays: formData.weeklyDays,
        selectedDays: formData.selectedDays,
        preferences: formData.preferences,
        additionalNotes: formData.additionalNotes,
        
        // Personal Bests - flatten for easier LLM access
        personalBests: formData.personalBests,
        lifetimePB: extractPersonalBest(formData.personalBests, 'lifetime'),
        recentPB: extractPersonalBest(formData.personalBests, 'recent'),
        
        // Weekly Volume - ensure proper formatting
        weeklyVolume: formData.weeklyVolume,
        currentWeeklyVolume: formatWeeklyVolume(formData.weeklyVolume),
        
        // Target Race - ensure proper formatting
        targetRace: formData.targetRace,
        targetRaceDistance: formData.targetRace.distance,
        goalRaceDate: formatGoalRaceDate(formData.targetRace.raceDate),
        targetTime: formatTargetTime(formData.targetRace.targetTime),
        
        // Training capabilities
        canRunTwice: formData.canRunTwice,
        canTrainDoubles: formData.canRunTwice, // Add alias for LLM compatibility
        irregularSchedule: formData.irregularSchedule,
        
        // Check-in preference
        checkInFrequency: formData.checkInFrequency,
        checkInPreference: formData.checkInFrequency, // Add alias for LLM compatibility
        
        // Training days - format for LLM
        trainingDays: formatTrainingDays(formData.selectedDays, formData.irregularSchedule),
        
        // Training start date - convert empty string to null for cleaner backend handling
        trainingStartDate: formData.trainingStartDate || null
      };
      
      // Compact onboarding summary
      const targetDisplay = transformedData.targetRaceDistance 
        ? transformedData.targetTime 
          ? `${transformedData.targetRaceDistance} (${transformedData.targetTime})`
          : transformedData.targetRace.justFinish
            ? `${transformedData.targetRaceDistance} (just finish)`
            : transformedData.targetRaceDistance
        : 'No target race';
      debugLog('📋 [ONBOARDING] Submitting:', {
        target: targetDisplay,
        volume: transformedData.currentWeeklyVolume,
        days: transformedData.weeklyDays,
        startDate: transformedData.trainingStartDate
      });
      
      const startTime = Date.now();
      
      // Add timeout to prevent hanging
      const TIMEOUT_MS = 15000; // 15 second timeout to allow for database operations
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
      
      let completeResponse;
      try {
        completeResponse = await fetch(`${API_URL}/user/onboarding-complete`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await getToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            onboarding_data: transformedData
          }),
          signal: controller.signal
        });
        clearTimeout(timeoutId);
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          debugError(`❌ [ONBOARDING] API call timed out after ${TIMEOUT_MS / 1000} seconds`);
          // Proceed anyway since onboarding data is saved locally
          debugLog('🚀 [ONBOARDING] Proceeding with onboarding completion despite timeout');
          // Create a mock conversation and welcome message for immediate display
          if (user) {
            const mockWelcomeMessage = "Hey there! 🏃‍♂️ Welcome to Runium! I'm your personal running coach. I can see from your goals that you're aiming for something truly ambitious. I've created your training schedule based on your preferences - you can check it out in the Schedule tab. Feel free to chat with me anytime about your training, ask questions, or request adjustments to the schedule. I'm here to help you progress safely toward your goals! Pole pole - steady work builds strong legs.";
            debugLog('🚀 [ONBOARDING] Storing temporary welcome message for immediate display (no mock conversation id)');
            localStorage.setItem(`onboarding_welcome_message_${user.id}`, mockWelcomeMessage);
          }
        // Navigate immediately even on timeout
        debugLog('Timeout navigation - calling onComplete() immediately');
        setIsSubmitting(false);
        try {
          onComplete();
          debugLog('onComplete() called successfully after timeout');
        } catch (navError) {
          debugError('Error calling onComplete() after timeout:', navError);
        }
          return;
        }
        throw error; // Re-throw other errors
      }
      
      const completeTime = Date.now() - startTime;
      debugLog(`✅ [ONBOARDING] Data saved to backend (${completeTime}ms)`);
      
      if (!completeResponse.ok) {
        debugError('❌ [ONBOARDING] Failed to mark onboarding as complete');
        debugError('❌ [ONBOARDING] Status:', completeResponse.status);
        debugError('❌ [ONBOARDING] Status Text:', completeResponse.statusText);
        const errorData = await completeResponse.text();
        debugError('❌ [ONBOARDING] Error response:', errorData);
        
        // Still proceed to app even if onboarding API fails
        debugLog('🚀 [ONBOARDING] Proceeding to app despite onboarding API failure...');
        // Create a mock conversation and welcome message for immediate display
        if (user) {
          const mockWelcomeMessage = "Hey there! 🏃‍♂️ Welcome to Runium! I'm your personal running coach. I can see from your goals that you're aiming for something truly ambitious. I've created your training schedule based on your preferences - you can check it out in the Schedule tab. Feel free to chat with me anytime about your training, ask questions, or request adjustments to the schedule. I'm here to help you progress safely toward your goals! Pole pole - steady work builds strong legs.";
          debugLog('🚀 [ONBOARDING] Storing temporary welcome message for immediate display (no mock conversation id)');
          localStorage.setItem(`onboarding_welcome_message_${user.id}`, mockWelcomeMessage);
        }
        // Navigate immediately even on API failure
        debugLog('API failure navigation - calling onComplete() immediately');
        setIsSubmitting(false);
        
        // Reset schedule button glow animation for new users (even on API failure)
        localStorage.removeItem('schedule_button_clicked');
        
        try {
          onComplete();
          debugLog('onComplete() called successfully after API failure');
        } catch (error) {
          debugError('Error calling onComplete() after API failure:', error);
        }
        return; // Exit early since we're handling navigation above
      } else {
        await completeResponse.json(); // Response not needed, just verify success
        debugLog('✅ [ONBOARDING] Profile saved successfully');
        
        // Set flag to indicate onboarding was just completed
        localStorage.setItem(`onboarding_completed_${user?.id}`, 'true');
        
        // Reset schedule button glow animation for new users
        localStorage.removeItem('schedule_button_clicked');
        
        // Start schedule generation in background (non-blocking)
        debugLog('🏃 [SCHEDULE] Requesting schedule generation from backend...');
        const scheduleStartTime = Date.now();
        
        // 🎯 FIX: Get token first, then make fetch call
        getToken().then(token => {
          return fetch(`${API_URL}/user/generate-schedule`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          });
        }).then(generateResponse => {
          if (!generateResponse.ok) {
            debugError('❌ [SCHEDULE] Background schedule generation failed:', generateResponse.status);
          } else {
            return generateResponse.json();
          }
        }).then(scheduleData => {
          if (scheduleData) {
            const scheduleTime = Date.now() - scheduleStartTime;
            debugLog(`⏱️ [API] Schedule Generation API endpoint responded in ${scheduleTime}ms (${(scheduleTime/1000).toFixed(1)}s)`);
            debugLog(`🚀 [SCHEDULE GENERATION STARTED] Schedule creation initiated - this will take 10-15 seconds`);
            debugLog(`⏳ [SCHEDULE] Watch for completion message in console when schedule is ready`);
            debugLog(`📊 [SCHEDULE] API Response:`, scheduleData);
            
              // 🎯 Store the conversation ID and welcome message for immediate display
              if (scheduleData.conversation_id && user) {
                debugLog('🚀 [ONBOARDING] Storing conversation ID:', scheduleData.conversation_id);
                localStorage.setItem(`onboarding_conversation_id_${user.id}`, scheduleData.conversation_id.toString());
                
                // 🎯 Store schedule start time for timing calculations
                localStorage.setItem(`schedule_start_time_${user.id}`, Date.now().toString());
                
                // 🎯 RESTORED: Store welcome message for immediate display (was removed during SSE implementation)
                if (scheduleData.welcome_message) {
                  debugLog('🚀 [ONBOARDING] Storing welcome message for immediate display');
                  localStorage.setItem(`onboarding_welcome_message_${user.id}`, scheduleData.welcome_message);
                } else {
                  // 🎯 FALLBACK: Create a default welcome message if none provided
                  const defaultWelcome = "Hey there! 🏃‍♂️ Welcome to Runium! I'm your personal running coach.";
                  debugLog('🚀 [ONBOARDING] No welcome message provided, storing default');
                  localStorage.setItem(`onboarding_welcome_message_${user.id}`, defaultWelcome);
                }
                
                // 🎯 NEW: Store loading message (Message 2) for immediate display
                if (scheduleData.loading_message) {
                  debugLog('🚀 [ONBOARDING] Storing loading message for immediate display');
                  localStorage.setItem(`onboarding_loading_message_${user.id}`, scheduleData.loading_message);
                } else {
                  debugLog('⚠️ [ONBOARDING] No loading message provided in response');
                }
              
              // Trigger immediate chat refresh to display welcome and loading messages
              debugLog('🚀 [ONBOARDING] Triggering immediate chat refresh to display welcome and loading messages');
              window.dispatchEvent(new Event('runium:refresh-chat'));
            } else {
              debugWarn('⚠️ [ONBOARDING] No conversation ID in response, chat will create new conversation');
            }
            
      // 🎯 FIX: Removed setTimeout-based refresh - refresh will be triggered by onboarding_success event
      // from the backend when schedule generation actually completes
            
            // Clean up the onboarding flag
            setTimeout(() => {
              localStorage.removeItem(`onboarding_completed_${user?.id}`);
              debugLog('🧹 [ONBOARDING] Cleaned up onboarding completion flag');
            }, 1000);
          }
        }).catch(error => {
          debugError('❌ [SCHEDULE] Background schedule generation error:', error);
          // User will see loading state in dashboard, and can regenerate later if needed
        });
      }
      
      // Navigate immediately since schedule generation is now in background
      debugLog('Navigating immediately to paywall...');
      setIsSubmitting(false);
      debugLog('isSubmitting set to false, calling onComplete() immediately');
      try {
        onComplete();
        debugLog('onComplete() called successfully');
      } catch (error) {
        debugError('Error calling onComplete():', error);
      }
    } catch (error) {
      debugError('Error submitting onboarding:', error);
      // Still complete onboarding to avoid blocking the user
      debugLog('🚀 [ONBOARDING] Error occurred, scheduling navigation in 2 seconds...');
      // Create a mock conversation and welcome message for immediate display
      if (user) {
        const mockWelcomeMessage = "Hey there! 🏃‍♂️ Welcome to Runium! I'm your personal running coach. I can see from your goals that you're aiming for something truly ambitious. I've created your training schedule based on your preferences - you can check it out in the Schedule tab. Feel free to chat with me anytime about your training, ask questions, or request adjustments to the schedule. I'm here to help you progress safely toward your goals! Pole pole - steady work builds strong legs.";
        debugLog('🚀 [ONBOARDING] Storing temporary welcome message for immediate display (no mock conversation id)');
        localStorage.setItem(`onboarding_welcome_message_${user.id}`, mockWelcomeMessage);
      }
      // Navigate immediately even on error
      debugLog('Error navigation - calling onComplete() immediately');
      setIsSubmitting(false);
      debugLog('isSubmitting set to false after error, calling onComplete() immediately');
      try {
        onComplete();
        debugLog('onComplete() called successfully after error');
      } catch (navError) {
        debugError('Error calling onComplete() after error:', navError);
      }
    }
  };

  // Show loading state while checking for existing data
  if (isLoadingExistingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300">Loading your responses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">

        {/* Main content */}
        <div className={`bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-700/70 overflow-hidden transition-all duration-300 ${
          isAnimating ? 'scale-95 opacity-80' : 'scale-100 opacity-100'
        }`}>
          {/* Header */}
          <div className="bg-blue-500/10 px-6 py-6 border-b border-gray-700/50 text-center">
            <h2 className="text-2xl font-bold text-white mb-2">
              {currentStepData.title}
            </h2>
            {currentStepData.id !== 'weeklyVolume' && (
              <p className="text-gray-300">
                {currentStepData.subtitle}
              </p>
            )}
          </div>

          {/* Content */}
          <div className="p-8">
            {currentStepData.type === 'info' && (
              <div>
                {currentStepData.content}
                <div className="mt-8 text-center">
                  <button
                    onClick={handleNext}
                    className="bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors shadow-md shadow-blue-500/20 active:scale-95"
                  >
                    {currentStepData.action}
                  </button>
                </div>
              </div>
            )}

            {currentStepData.type === 'choice' && (
              <div className="grid gap-4">
                {currentStepData.choices.map((choice) => (
                  <button
                    key={choice.id}
                    onClick={() => handleChoice(choice.id)}
                    className="group bg-gray-700/50 hover:bg-gray-700 border border-gray-600 hover:border-blue-500 rounded-lg p-4 text-left transition-all duration-200 hover:shadow-lg active:scale-[0.98]"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="text-2xl">{choice.icon}</div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-white group-hover:text-blue-400 transition-colors">
                          {choice.title}
                        </h3>
                        <p className="text-sm text-gray-400 mt-1">
                          {choice.description}
                        </p>
                      </div>
                      <div className="text-gray-400 group-hover:text-blue-400 transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {currentStepData.type === 'multiple-choice' && (
              <div>
                <div className="grid gap-2 mb-6">
                  {currentStepData.choices.map((choice) => {
                    const isSelected = formData[currentStepData.id]?.includes(choice.id);
                    return (
                      <button
                        key={choice.id}
                        onClick={() => handleChoice(choice.id, true)}
                        className={`group border rounded-lg py-3 px-4 text-left transition-all duration-200 hover:shadow-lg active:scale-[0.99] ${
                          isSelected
                            ? 'bg-blue-600/20 border-blue-500 shadow-lg'
                            : 'bg-gray-700/50 hover:bg-gray-700 border-gray-600 hover:border-blue-500'
                        }`}
                      >
                        <div className="flex items-center space-x-4">
                          <div className="text-2xl">{choice.icon}</div>
                          <div className="flex-1">
                            <h3 className={`font-semibold transition-colors ${
                              isSelected ? 'text-blue-400' : 'text-white group-hover:text-blue-400'
                            }`}>
                              {choice.title}
                            </h3>
                            {choice.description && (
                              <p className="text-sm text-gray-400 mt-1">
                                {choice.description}
                              </p>
                            )}
                          </div>
                          <div className={`transition-colors ${
                            isSelected ? 'text-blue-400' : 'text-gray-400 group-hover:text-blue-400'
                          }`}>
                            {isSelected ? (
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <div className="w-5 h-5 border-2 border-current rounded" />
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <OnboardingFooter
                  visibleSteps={visibleSteps}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  canProceed={canProceedWithMultiple()}
                  onBack={handleBack}
                  onNext={handleNext}
                  primaryLabel="Continue"
                />
              </div>
            )}

            {currentStepData.type === 'form' && (
              <div className="self-start w-full">
                {currentStepData.content}
                <OnboardingFooter
                  visibleSteps={visibleSteps}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  canProceed={canProceedWithMultiple()}
                  onBack={handleBack}
                  onNext={handleNext}
                  primaryLabel="Continue"
                />
              </div>
            )}


            {currentStepData.type === 'completion' && (
              <div>
                {currentStepData.content}
                <div className="flex justify-center gap-4 mt-8">
                  {currentStep > 0 && (
                    <button
                      onClick={handleBack}
                      className="bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-12 rounded-lg transition-all duration-200 shadow-lg"
                    >
                      Back
                    </button>
                  )}
                  <button
                    onClick={handleNext}
                    className="bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white font-semibold py-3 px-12 rounded-lg transition-colors shadow-md shadow-blue-500/20 active:scale-95"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

// Form components - updated to match mockup functionality

const PersonalBestsForm = ({ formData, setFormData }) => {
  const currentYear = new Date().getFullYear();
  const allYears = Array.from({length: currentYear - 1969}, (_, i) => currentYear - i);
  
  // Ensure personalBests has the correct structure
  const personalBests = formData.personalBests || {
    pbsOrder: [],
    lifetimePBs: {},
    recentPBs: {},
    lifetimeYears: {},
    recentYears: {}
  };
  
  // Ensure all required fields exist
  const safePersonalBests = {
    pbsOrder: personalBests.pbsOrder || [],
    lifetimePBs: personalBests.lifetimePBs || {},
    recentPBs: personalBests.recentPBs || {},
    lifetimeYears: personalBests.lifetimeYears || {},
    recentYears: personalBests.recentYears || {}
  };
  
  const WORLD_RECORDS = {
    '5K': 755, // 12:35
    '10K': 1571, // 26:11
    'Half Marathon': 3402, // 56:42
    'Marathon': 7235 // 2:00:35
  };
  
  const PB_RANGES = {
    '5K': { min: WORLD_RECORDS['5K'], max: 3000, step: 1, label: '5K' },
    '10K': { min: WORLD_RECORDS['10K'], max: 5400, step: 1, label: '10K' },
    'Half Marathon': { min: WORLD_RECORDS['Half Marathon'], max: 10800, step: 1, label: 'Half Marathon' },
    'Marathon': { min: WORLD_RECORDS['Marathon'], max: 21600, step: 1, label: 'Marathon' },
    'Other Distance': { min: 0, max: 1000, step: 1, label: 'Other Distance' }
  };

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const paddedSeconds = seconds.toString().padStart(2, '0');
    const paddedMinutes = minutes.toString().padStart(2, '0');
    if (hours > 0) return `${hours}:${paddedMinutes}:${paddedSeconds}`;
    return `${paddedMinutes}:${paddedSeconds}`;
  };

  const handleAddDistance = (distance) => {
    if (distance === 'Other Distance') {
      // For Other Distance, clear all other selections and only select Other Distance
      setFormData(prev => ({
        ...prev,
        personalBests: {
          ...prev.personalBests,
          pbsOrder: ['Other Distance'],
          lifetimePBs: {},
          recentPBs: {}
        }
      }));
    } else {
      // For regular distances, add to existing selections (but remove Other Distance if it exists)
      const currentOrder = (safePersonalBests.pbsOrder || []).filter(d => d !== 'Other Distance');
      const newOrder = [...currentOrder, distance];
      const config = PB_RANGES[distance];
      const defaultValue = config.min;
      
      setFormData(prev => ({
        ...prev,
        personalBests: {
          ...prev.personalBests,
          pbsOrder: newOrder,
          lifetimePBs: { ...prev.personalBests.lifetimePBs, [distance]: defaultValue },
          recentPBs: { ...prev.personalBests.recentPBs, [distance]: defaultValue }
        }
      }));
    }
  };

  const handleRemoveDistance = (distance) => {
    const newOrder = (safePersonalBests.pbsOrder || []).filter(d => d !== distance);
    setFormData(prev => {
      const newPersonalBests = { ...prev.personalBests };
      newPersonalBests.pbsOrder = newOrder;
      
      // Remove the distance data completely when removing
      delete newPersonalBests.lifetimePBs[distance];
      delete newPersonalBests.recentPBs[distance];
      delete newPersonalBests.lifetimeYears[distance];
      delete newPersonalBests.recentYears[distance];
      
      return {
        ...prev,
        personalBests: newPersonalBests
      };
    });
  };

  const handlePbChange = (distance, type, value) => {
    const config = PB_RANGES[distance];
    const numericValue = parseInt(value);
    
    setFormData(prev => {
      const newPersonalBests = { ...prev.personalBests };
      
      if (type === 'lifetime') {
        newPersonalBests.lifetimePBs = { ...newPersonalBests.lifetimePBs, [distance]: value };
        // Always update recent PB to match lifetime when lifetime changes
        // This creates the "synced" behavior
        newPersonalBests.recentPBs = { ...newPersonalBests.recentPBs, [distance]: value };
      } else if (type === 'recent') {
        const lifetimeValue = parseInt(newPersonalBests.lifetimePBs[distance]) || config.min;
        if (numericValue >= lifetimeValue) {
          newPersonalBests.recentPBs = { ...newPersonalBests.recentPBs, [distance]: value };
        }
      } else if (type === 'lifetimeYear') {
        newPersonalBests.lifetimeYears = { ...newPersonalBests.lifetimeYears, [distance]: value };
      } else if (type === 'recentYear') {
        newPersonalBests.recentYears = { ...newPersonalBests.recentYears, [distance]: value };
      }
      
      return { ...prev, personalBests: newPersonalBests };
    });
  };

  return (
    <div className="space-y-6">
      {/* Distance selector */}
      <div className="space-y-3">
        {/* Regular distances in 2x2 grid */}
        <div className="grid grid-cols-2 gap-3">
          {Object.keys(PB_RANGES).filter(distance => distance !== 'Other Distance').map(distance => {
            const isAdded = (safePersonalBests.pbsOrder || []).includes(distance);
            return (
              <button
                key={distance}
                onClick={() => !isAdded && handleAddDistance(distance)}
                disabled={isAdded}
                className={`p-3 text-center rounded-lg border-2 font-semibold transition-all ${
                  isAdded 
                    ? 'bg-blue-600/20 border-blue-500 text-blue-400' 
                    : 'bg-gray-700/30 border-gray-600 text-gray-300 hover:border-gray-500 cursor-pointer'
                }`}
              >
                {PB_RANGES[distance].label}
              </button>
            );
          })}
        </div>
        
        {/* Other Distance button - full width */}
        <button
          onClick={() => !(safePersonalBests.pbsOrder || []).includes('Other Distance') && handleAddDistance('Other Distance')}
          disabled={(safePersonalBests.pbsOrder || []).includes('Other Distance')}
          className={`group relative w-full p-3 text-center rounded-lg border-2 font-semibold transition-all ${
            (safePersonalBests.pbsOrder || []).includes('Other Distance')
              ? 'bg-blue-600/20 border-blue-500 text-blue-400' 
              : 'bg-gray-700/30 border-gray-600 text-gray-300 hover:border-gray-500 cursor-pointer'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <span>Other Distance</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-xs text-white rounded transition-opacity">
            AI coach will gather your personal best after onboarding
          </div>
        </button>
      </div>

      {/* PB sliders for added distances (excluding Other Distance) */}
      {(safePersonalBests.pbsOrder || []).filter(distance => distance !== 'Other Distance').map(distance => {
        const config = PB_RANGES[distance];
        const lifetimeValue = safePersonalBests.lifetimePBs[distance] || config.min;
        const recentValue = safePersonalBests.recentPBs[distance] || lifetimeValue;
        const lifetimeYear = safePersonalBests.lifetimeYears[distance];
        const recentYear = safePersonalBests.recentYears[distance];
        
        const recentYears = lifetimeYear ? allYears.filter(y => y >= lifetimeYear) : allYears;
        
        const worldRecord = WORLD_RECORDS[distance];
        const isLifetimeWR = worldRecord && parseInt(lifetimeValue) === worldRecord;
        const isRecentWR = worldRecord && parseInt(recentValue) === worldRecord;

        return (
          <div key={distance} className="border border-gray-600 rounded-lg p-4 bg-gray-700/30">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold text-white">{config.label}</h4>
              <button
                onClick={() => handleRemoveDistance(distance)}
                className="text-red-400 hover:text-red-300 text-sm font-medium"
              >
                Remove
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Lifetime PB */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-300">Lifetime PB</span>
                  <span className="font-semibold text-blue-400 flex items-center">
                    {isLifetimeWR && (
                      <span className="text-green-400 font-bold mr-2 text-xs bg-green-900/50 px-1.5 py-0.5 rounded">
                        WR
                      </span>
                    )}
                    {formatTime(parseInt(lifetimeValue))}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    className="flex-grow"
                    value={lifetimeValue}
                    onChange={(e) => handlePbChange(distance, 'lifetime', e.target.value)}
                    min={config.min}
                    max={config.max}
                    step={config.step}
                  />
                  <select
                    value={lifetimeYear || ''}
                    onChange={(e) => handlePbChange(distance, 'lifetimeYear', e.target.value)}
                    className="w-20 px-2 py-1 bg-gray-700 border border-gray-600 rounded-md text-white text-xs"
                  >
                    <option value="">Year</option>
                    {allYears.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Recent PB */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-300">Recent PB</span>
                  <span className="font-semibold text-blue-400 flex items-center">
                    {isRecentWR && (
                      <span className="text-green-400 font-bold mr-2 text-xs bg-green-900/50 px-1.5 py-0.5 rounded">
                        WR
                      </span>
                    )}
                    {formatTime(parseInt(recentValue))}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    className="flex-grow"
                    value={recentValue}
                    onChange={(e) => handlePbChange(distance, 'recent', e.target.value)}
                    min={config.min}
                    max={config.max}
                    step={config.step}
                  />
                  <select
                    value={recentYear || ''}
                    onChange={(e) => handlePbChange(distance, 'recentYear', e.target.value)}
                    className="w-20 px-2 py-1 bg-gray-700 border border-gray-600 rounded-md text-white text-xs"
                  >
                    <option value="">Year</option>
                    {recentYears.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const WeeklyVolumeForm = ({ formData, setFormData, onAutoAdvance }) => {
  const handleVolumeChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      weeklyVolume: {
        ...prev.weeklyVolume,
        [field]: value
      }
    }));
  };

  const { type, value, unit, minutes } = formData.weeklyVolume;

  return (
    <div className="space-y-6">
          {/* Type toggle */}
          <div className="flex gap-2 p-1 bg-gray-700/50 rounded-lg">
            <button
              onClick={() => handleVolumeChange('type', 'distance')}
              className={`flex-1 py-2 px-3 rounded-md text-sm transition-colors ${
                type === 'distance'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-300 hover:bg-gray-600'
              }`}
            >
              Distance
            </button>
            <button
              onClick={() => handleVolumeChange('type', 'time')}
              className={`flex-1 py-2 px-3 rounded-md text-sm transition-colors ${
                type === 'time'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-300 hover:bg-gray-600'
              }`}
            >
              Time
            </button>
          </div>

          {type === 'distance' ? (
            <div className="space-y-6">
              <div className="text-center">
                <span className="text-3xl font-bold text-blue-400">
                  {value} {unit}
                </span>
                <p className="text-gray-400 text-sm mt-1">Slide to your weekly distance</p>
              </div>
              
              <input
                type="range"
                min="0"
                max="150"
                value={value}
                onChange={(e) => handleVolumeChange('value', parseInt(e.target.value))}
                className="w-full"
              />
              
              {/* Unit toggle */}
              <div className="flex justify-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="km"
                    checked={unit === 'km'}
                    onChange={(e) => handleVolumeChange('unit', e.target.value)}
                    className="w-4 h-4 text-blue-500 bg-gray-700 border-gray-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-gray-300">Kilometers</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="mi"
                    checked={unit === 'mi'}
                    onChange={(e) => handleVolumeChange('unit', e.target.value)}
                    className="w-4 h-4 text-blue-500 bg-gray-700 border-gray-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-gray-300">Miles</span>
                </label>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <span className="text-3xl font-bold text-blue-400">
                  {Math.floor(minutes / 60)}h {minutes % 60}m
                </span>
                <p className="text-gray-400 text-sm mt-1">Slide to your weekly time</p>
              </div>
              
              <input
                type="range"
                min="0"
                max="1200"
                step="15"
                value={minutes}
                onChange={(e) => handleVolumeChange('minutes', parseInt(e.target.value))}
                className="w-full"
              />
            </div>
          )}
    </div>
  );
};

const GoalRaceForm = ({ formData, setFormData }) => {
  const distances = ['5K', '10K', 'Half Marathon', 'Marathon'];
  
  const WORLD_RECORDS = {
    '5K': 755, // 12:35
    '10K': 1571, // 26:11
    'Half Marathon': 3402, // 56:42
    'Marathon': 7235 // 2:00:35
  };
  
  const GOAL_TIME_RANGES = {
    '5K': { min: WORLD_RECORDS['5K'], max: 3600, step: 1 },
    '10K': { min: WORLD_RECORDS['10K'], max: 7200, step: 1 },
    'Half Marathon': { min: WORLD_RECORDS['Half Marathon'], max: 14400, step: 1 },
    'Marathon': { min: WORLD_RECORDS['Marathon'], max: 25200, step: 1 }
  };

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const paddedSeconds = seconds.toString().padStart(2, '0');
    const paddedMinutes = minutes.toString().padStart(2, '0');
    if (hours > 0) return `${hours}:${paddedMinutes}:${paddedSeconds}`;
    return `${paddedMinutes}:${paddedSeconds}`;
  };

  const handleDistanceSelect = (distance) => {
    const newConf = GOAL_TIME_RANGES[distance];
    setFormData(prev => ({
      ...prev,
      targetRace: {
        ...prev.targetRace,
        distance: distance,
        // Only set targetTime if distance has a config (not "Other Distance")
        // If distance is cleared or "Other Distance", set targetTime to null
        targetTime: newConf ? { totalSeconds: newConf.min } : null
      }
    }));
  };

  const goalConfig = GOAL_TIME_RANGES[formData.targetRace.distance];
  // Only use goalConfig.min as fallback if we have a distance but no targetTime yet
  // If no distance is selected, goalTimeValue should be 0/null
  const goalTimeValue = formData.targetRace.targetTime?.totalSeconds ?? (goalConfig ? goalConfig.min : null);
  const worldRecord = formData.targetRace.distance ? WORLD_RECORDS[formData.targetRace.distance] : null;
  const isWorldRecord = worldRecord && goalTimeValue && parseInt(goalTimeValue) === worldRecord;

  return (
    <div className="space-y-6">
      {/* Distance selector */}
      <div>
        <span className="text-gray-300 font-medium block mb-3">Distance</span>
        <div className="grid grid-cols-2 gap-3">
          {distances.map(distance => {
            const isSelected = formData.targetRace.distance === distance;
            return (
              <button
                key={distance}
                onClick={() => handleDistanceSelect(distance)}
                className={`p-3 text-center rounded-lg cursor-pointer border-2 font-semibold transition-all ${
                  isSelected 
                    ? 'bg-blue-600/20 border-blue-500 text-blue-400' 
                    : 'bg-gray-700/30 border-gray-600 text-gray-300 hover:border-gray-500'
                }`}
              >
                {distance}
              </button>
            );
          })}
          <button
            onClick={() => handleDistanceSelect('Other Distance')}
            className={`group relative p-3 text-center rounded-lg cursor-pointer border-2 font-semibold transition-all col-span-2 ${
              formData.targetRace.distance === 'Other Distance'
                ? 'bg-blue-600/20 border-blue-500 text-blue-400'
                : 'bg-gray-700/30 border-gray-600 text-gray-300 hover:border-gray-500'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <span>Other Distance</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-xs text-white rounded transition-opacity">
              AI coach will customize your race distance after onboarding
            </div>
          </button>
        </div>
      </div>

      {/* Time goal slider */}
      {goalConfig && !formData.targetRace.justFinish && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-300 font-medium">Time Goal</span>
            <span className="font-semibold text-blue-400 flex items-center">
              {isWorldRecord && (
                <span className="text-green-400 font-bold mr-2 text-xs bg-green-900/50 px-1.5 py-0.5 rounded">
                  WR
                </span>
              )}
              {goalTimeValue ? formatTime(parseInt(goalTimeValue)) : '--:--'}
            </span>
          </div>
          <input
            type="range"
            min={goalConfig.min}
            max={goalConfig.max}
            step={goalConfig.step}
            value={goalTimeValue || goalConfig.min}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              targetRace: {
                ...prev.targetRace,
                targetTime: { totalSeconds: parseInt(e.target.value) }
              }
            }))}
            className="w-full"
          />
        </div>
      )}

      {/* Just want to finish checkbox */}
      <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-700/30 cursor-pointer transition-colors">
        <input
          type="checkbox"
          checked={formData.targetRace.justFinish || false}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            targetRace: { ...prev.targetRace, justFinish: e.target.checked }
          }))}
          className="w-4 h-4 text-blue-500 bg-gray-700 border-gray-600 focus:ring-blue-500 rounded"
        />
        <span className="text-gray-300">Just want to finish</span>
      </label>

      {/* Race date */}
      <div>
        <span className="text-gray-300 font-medium block mb-2">Race Date</span>
        <DatePicker
          value={formData.targetRace.raceDate || ''}
          onChange={(date) => setFormData(prev => ({
            ...prev,
            targetRace: { ...prev.targetRace, raceDate: date }
          }))}
          minDate={(() => {
            // Get today's date in local timezone (YYYY-MM-DD format)
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          })()}
        />
      </div>
    </div>
  );
};

const WeeklyDaysForm = ({ formData, setFormData }) => {
  const isIrregular = formData.irregularSchedule || false;
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const fullDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  // Initialize selectedDays if not present
  const selectedDays = formData.selectedDays || [];
  
  // Initialize training start date if not present
  const trainingStartDate = formData.trainingStartDate || '';
  
  const handleDayToggle = (dayIndex) => {
    if (isIrregular) return;
    
    const newSelectedDays = selectedDays.includes(dayIndex)
      ? selectedDays.filter(d => d !== dayIndex)
      : [...selectedDays, dayIndex];
    
    setFormData(prev => ({ 
      ...prev, 
      selectedDays: newSelectedDays,
      weeklyDays: newSelectedDays.length 
    }));
  };
  
  const handleIrregularToggle = (checked) => {
    setFormData(prev => ({ 
      ...prev, 
      irregularSchedule: checked,
      // Allow re-selection of days after unchecking irregular
    }));
  };

  return (
    <div className="space-y-3">
      {/* Days selector */}
      <div>
        <div className={`grid grid-cols-7 gap-3 transition-opacity ${
          isIrregular ? 'opacity-50' : 'opacity-100'
        }`}>
          {days.map((day, index) => (
            <button
              key={index}
              onClick={() => handleDayToggle(index)}
              disabled={isIrregular}
              title={fullDays[index]}
              className={`day-box-label ${isIrregular ? 'cursor-not-allowed' : 'cursor-pointer'} ${
                selectedDays.includes(index) && !isIrregular
                  ? 'bg-blue-600/20 border-blue-500 text-blue-400'
                  : 'bg-gray-700/30 border-gray-600 text-gray-300 hover:border-gray-500'
              } border-2 rounded-lg font-semibold transition-all flex flex-col justify-center items-center min-w-[44px] min-h-[44px] px-1 active:scale-95`}
            >
              <div className="text-xs">{day}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Can run twice a day */}
      <div>
        <input
          type="checkbox"
          id="can_run_twice_check"
          checked={formData.canRunTwice || false}
          onChange={(e) => setFormData(prev => ({ ...prev, canRunTwice: e.target.checked }))}
          className="sr-only"
        />
        <label
          htmlFor="can_run_twice_check"
          className={`day-box-label cursor-pointer text-sm ${
            formData.canRunTwice
              ? 'bg-blue-600/20 border-blue-500 text-blue-400'
              : 'bg-gray-700/30 border-gray-600 text-gray-300 hover:border-gray-500'
          } p-4 border-2 rounded-lg font-semibold transition-all flex justify-center items-center`}
        >
          Can run twice a day
        </label>
      </div>

      {/* Irregular schedule */}
      <div>
        <input
          type="checkbox"
          id="irregular_schedule_check"
          checked={isIrregular}
          onChange={(e) => handleIrregularToggle(e.target.checked)}
          className="sr-only"
        />
        <label
          htmlFor="irregular_schedule_check"
          className={`day-box-label cursor-pointer text-sm ${
            isIrregular
              ? 'bg-blue-600/20 border-blue-500 text-blue-400'
              : 'bg-gray-700/30 border-gray-600 text-gray-300 hover:border-gray-500'
          } p-4 border-2 rounded-lg font-semibold transition-all flex justify-center items-center`}
        >
          No fixed days – irregular schedule
        </label>
      </div>

      {/* Training Start Date */}
      <div>
        <span className="text-gray-300 font-medium block mb-2">When do you want to start training?</span>
        <DatePicker
          value={trainingStartDate}
          onChange={(date) => setFormData(prev => ({ ...prev, trainingStartDate: date }))}
          minDate={(() => {
            // Get today's date in local timezone (YYYY-MM-DD format)
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          })()}
        />
      </div>
    </div>
  );
};


const PreferencesForm = ({ formData, setFormData }) => {
  const preferences = [
    'past_or_current_injury',
    'training_in_another_sport', 
    'pregnant_or_postpartum',
    'returning_after_long_break',
    'let_me_talk_to_coach'
  ];

  const preferenceLabels = {
    'past_or_current_injury': 'Past or current injury',
    'training_in_another_sport': 'Training in another sport',
    'pregnant_or_postpartum': 'Pregnant or postpartum',
    'returning_after_long_break': 'Returning after a long break',
    'let_me_talk_to_coach': 'Let me talk to the coach'
  };

  const handlePreferenceChange = (pref, checked) => {
    const currentPrefs = formData.preferences || [];
    const newPrefs = checked
      ? [...currentPrefs, pref]
      : currentPrefs.filter(p => p !== pref);
    
    setFormData(prev => ({ ...prev, preferences: newPrefs }));
  };

  return (
    <div className="space-y-4">
      {preferences.map(pref => {
        const isChecked = (formData.preferences || []).includes(pref);
        return (
          <div key={pref}>
            <input
              type="checkbox"
              id={`pref_${pref}`}
              checked={isChecked}
              onChange={(e) => handlePreferenceChange(pref, e.target.checked)}
              className="sr-only"
            />
            <label
              htmlFor={`pref_${pref}`}
              className={`day-box-label cursor-pointer text-sm text-center ${
                isChecked
                  ? 'bg-blue-600/20 border-blue-500 text-blue-400'
                  : 'bg-gray-700/30 border-gray-600 text-gray-300 hover:border-gray-500'
              } p-4 border-2 rounded-lg font-semibold transition-all flex justify-center items-center`}
            >
              {preferenceLabels[pref]}
            </label>
          </div>
        );
      })}
    </div>
  );
};

// Add CSS for custom range sliders and day boxes
// Range-slider styling lives in index.css to avoid module-load DOM side effects.

export default Onboarding;
